import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  limit
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import {
  verifyEmployeeInvitationToken,
  consumeEmployeeInvitationToken,
} from '../../services/employeeInviteApi';

const PENDING_REGISTRATIONS_COLLECTION = 'pendingRegistrations';

// Async thunk to submit a new registration
export const submitRegistration = createAsyncThunk(
  'registration/submitRegistration',
  async (_payload, { rejectWithValue }) => {
    return rejectWithValue(
      'Open registration is disabled. Please use your invitation link from admin.'
    );
  }
);

export const completeInviteSignup = createAsyncThunk(
  'registration/completeInviteSignup',
  async ({ token, email, password, displayName }, { rejectWithValue }) => {
    try {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!token || !normalizedEmail || !password) {
        return rejectWithValue('token, email and password are required');
      }

      const verifyRes = await verifyEmployeeInvitationToken(token);
      if (!verifyRes?.email) {
        return rejectWithValue('Invitation verification failed');
      }
      if (String(verifyRes.email).trim().toLowerCase() !== normalizedEmail) {
        return rejectWithValue('Invitation email mismatch');
      }

      const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = credential.user;

      const userDocRef = doc(db, 'myeliners', normalizedEmail);
      const existingDoc = await getDocs(query(collection(db, 'myeliners'), where('email', '==', normalizedEmail), limit(1)));

      const minimalProfile = {
        name: String(displayName || normalizedEmail.split('@')[0]).trim(),
        email: normalizedEmail,
        userType: 'user',
        myelinLive: true,
        profileCompletionRequired: true,
        inviteAcceptedAt: serverTimestamp(),
        joinDate: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      if (existingDoc.empty) {
        await setDoc(userDocRef, minimalProfile, { merge: true });
      }
      await setDoc(userDocRef, minimalProfile, { merge: true });

      await consumeEmployeeInvitationToken({ token, email: normalizedEmail });

      return {
        uid: user.uid,
        email: normalizedEmail,
      };
    } catch (error) {
      if (error?.code === 'auth/email-already-in-use') {
        return rejectWithValue('This email has already been used to create an account.');
      }
      if (error?.code === 'auth/weak-password') {
        return rejectWithValue('Password should be at least 6 characters long.');
      }
      if (error?.code === 'auth/invalid-email') {
        return rejectWithValue('Please enter a valid email address.');
      }
      try {
        if (auth.currentUser) {
          await signOut(auth);
        }
      } catch (_signOutError) {
        // no-op
      }
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch all pending registrations (admin only)
export const fetchPendingRegistrations = createAsyncThunk(
  'registration/fetchPendingRegistrations',
  async (_, { rejectWithValue }) => {
    try {
      console.log('📋 Fetching pending registrations...');
      
      const querySnapshot = await getDocs(
        query(
          collection(db, PENDING_REGISTRATIONS_COLLECTION),
          orderBy('submittedAt', 'desc')
        )
      );
      
      const registrations = [];
      querySnapshot.forEach(doc => {
        registrations.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('✅ Fetched pending registrations:', registrations.length);
      return registrations;
    } catch (error) {
      console.error('❌ Error fetching pending registrations:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to approve a registration
export const approveRegistration = createAsyncThunk(
  'registration/approveRegistration',
  async ({ registrationId, adminRemarks }, { rejectWithValue, getState }) => {
    try {
      console.log('✅ Approving registration:', registrationId);
      
      // Get the registration data
      const registrationRef = doc(db, PENDING_REGISTRATIONS_COLLECTION, registrationId);
      const registrationDoc = await getDocs(query(collection(db, PENDING_REGISTRATIONS_COLLECTION), where('__name__', '==', registrationId)));
      
      if (registrationDoc.empty) {
        return rejectWithValue('Registration not found');
      }
      
      const registrationData = registrationDoc.docs[0].data();
      
      // Update registration status
      await updateDoc(registrationRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: 'admin', // In a real app, you'd use the admin's ID
        adminRemarks,
        lastUpdated: serverTimestamp()
      });
      
      // Move to active employees collection
      const employeeData = {
        name: registrationData.name,
        email: registrationData.email,
        personalEmail: registrationData.personalEmail,
        empID: registrationData.empID,
        designation: registrationData.designation,
        organogram: registrationData.organogram,
        dob: registrationData.dob,
        phone: registrationData.phone,
        whatsApp: registrationData.whatsApp,
        address: registrationData.address,
        country: registrationData.country,
        location: registrationData.location,
        leaveEntitlement: parseInt(registrationData.leaveEntitlement || 21, 10),
        leaveBalance: parseInt(registrationData.leaveEntitlement || 21, 10),
        leaveTaken: 0,
        emergencyContact: registrationData.emergencyContact,
        emergencyNumber: registrationData.emergencyNumber,
        emergencyRelation: registrationData.emergencyRelation,
        preferredLang: registrationData.preferredLang,
        allLang: registrationData.allLang,
        bio: registrationData.bio || '',
        userType: 'user',
        img: registrationData.img || '',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        myelinLive: true,
        joinDate: new Date().toISOString().split('T')[0] // Today's date
      };
      
      // Add to myeliners collection using email as document ID
      const employeeRef = doc(db, 'myeliners', registrationData.email);
      await setDoc(employeeRef, employeeData);
      
      console.log('✅ Registration approved and employee added with email as ID:', registrationData.email);
      
      return {
        registrationId,
        employeeId: registrationData.email,
        status: 'approved'
      };
    } catch (error) {
      console.error('❌ Error approving registration:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to reject a registration
export const rejectRegistration = createAsyncThunk(
  'registration/rejectRegistration',
  async ({ registrationId, adminRemarks }, { rejectWithValue }) => {
    try {
      console.log('❌ Rejecting registration:', registrationId);
      
      const registrationRef = doc(db, PENDING_REGISTRATIONS_COLLECTION, registrationId);
      
      await updateDoc(registrationRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: 'admin', // In a real app, you'd use the admin's ID
        adminRemarks,
        lastUpdated: serverTimestamp()
      });
      
      console.log('✅ Registration rejected successfully');
      
      return {
        registrationId,
        status: 'rejected'
      };
    } catch (error) {
      console.error('❌ Error rejecting registration:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete a registration
export const deleteRegistration = createAsyncThunk(
  'registration/deleteRegistration',
  async (registrationId, { rejectWithValue }) => {
    try {
      console.log('🗑️ Deleting registration:', registrationId);
      
      await deleteDoc(doc(db, PENDING_REGISTRATIONS_COLLECTION, registrationId));
      
      console.log('✅ Registration deleted successfully');
      
      return registrationId;
    } catch (error) {
      console.error('❌ Error deleting registration:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  registrations: [],
  loading: false,
  error: null,
  submitting: false,
  submitError: null,
  submitSuccess: false,
  approvingId: null,
  rejectingId: null,
  deletingId: null
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
    },
    clearSubmitSuccess: (state) => {
      state.submitSuccess = false;
    }
  },
  extraReducers: (builder) => {
    // Submit registration
    builder.addCase(submitRegistration.pending, (state) => {
      state.submitting = true;
      state.submitError = null;
      state.submitSuccess = false;
    });
    builder.addCase(submitRegistration.fulfilled, (state) => {
      state.submitting = false;
      state.submitSuccess = true;
      state.submitError = null;
    });
    builder.addCase(submitRegistration.rejected, (state, action) => {
      state.submitting = false;
      state.submitError = action.payload;
      state.submitSuccess = false;
    });

    // Complete invite signup
    builder.addCase(completeInviteSignup.pending, (state) => {
      state.submitting = true;
      state.submitError = null;
      state.submitSuccess = false;
    });
    builder.addCase(completeInviteSignup.fulfilled, (state) => {
      state.submitting = false;
      state.submitSuccess = true;
      state.submitError = null;
    });
    builder.addCase(completeInviteSignup.rejected, (state, action) => {
      state.submitting = false;
      state.submitError = action.payload;
      state.submitSuccess = false;
    });

    // Fetch pending registrations
    builder.addCase(fetchPendingRegistrations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPendingRegistrations.fulfilled, (state, action) => {
      state.loading = false;
      state.registrations = action.payload;
    });
    builder.addCase(fetchPendingRegistrations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Approve registration
    builder.addCase(approveRegistration.pending, (state, action) => {
      state.approvingId = action.meta.arg.registrationId;
    });
    builder.addCase(approveRegistration.fulfilled, (state, action) => {
      state.approvingId = null;
      // Remove from pending registrations
      state.registrations = state.registrations.filter(
        reg => reg.id !== action.payload.registrationId
      );
    });
    builder.addCase(approveRegistration.rejected, (state, action) => {
      state.approvingId = null;
      state.error = action.payload;
    });

    // Reject registration
    builder.addCase(rejectRegistration.pending, (state, action) => {
      state.rejectingId = action.meta.arg.registrationId;
    });
    builder.addCase(rejectRegistration.fulfilled, (state, action) => {
      state.rejectingId = null;
      // Update registration status in the list
      const index = state.registrations.findIndex(
        reg => reg.id === action.payload.registrationId
      );
      if (index !== -1) {
        state.registrations[index].status = 'rejected';
      }
    });
    builder.addCase(rejectRegistration.rejected, (state, action) => {
      state.rejectingId = null;
      state.error = action.payload;
    });

    // Delete registration
    builder.addCase(deleteRegistration.pending, (state, action) => {
      state.deletingId = action.meta.arg;
    });
    builder.addCase(deleteRegistration.fulfilled, (state, action) => {
      state.deletingId = null;
      // Remove from registrations list
      state.registrations = state.registrations.filter(
        reg => reg.id !== action.payload
      );
    });
    builder.addCase(deleteRegistration.rejected, (state, action) => {
      state.deletingId = null;
      state.error = action.payload;
    });
  }
});

export const { clearErrors, clearSubmitSuccess } = registrationSlice.actions;
export default registrationSlice.reducer;
